<template>
  <component
    :is="element"
    class="button"
    :class="[
      modifierClasses,
      {
        'show-state-icon': showStateIcon,
        'has-hover-icon': $slots.hoverIcon,
        'no-icon': !showIcon,
        'button--has-hover-icon-only': hoverOnly,
        'button--transition-hover-icon': transitionHover
      },
    ]"
    :href="url || null"
    :type="type || null"
  >
    <h3
      v-if="!labelHtml"
      class="button__label"
      :class="[
        labelClasses,
        { 'button__label--no-icon': !showIcon },
      ]"
      v-html="label"
    />

    <h3
      v-if="labelHtml"
      class="button__label"
      :class="labelClasses"
      v-html="labelHtml"
    />

    <span
      v-if="showIcon || $slots.hoverIcon"
      class="button__icon"
    >
      <slot
        v-if="showIcon"
        name="icon"
      >
        <icon-arrow-right />
      </slot>

      <span
        v-if="$slots.hoverIcon"
        class="button__hover-icon"
      >
        <slot name="hoverIcon" />
      </span>
    </span>

    <span
      v-if="showStateIcon && $slots.stateIcon"
      class="button__state"
    >
      <slot name="stateIcon" />
    </span>
  </component>
</template>

<script>

/**
 * Vue: Button (btn)
 * -----------------------------------------------------------------------------
 * Global interactive button element.
 * - Styles are imported in critical styles.
 * - Must be imported as <btn> as <button> is a default element.
 *
 * @param {String} [classLabel] - Label class.
 * @param {String} [customElement] - Optional override to set button element.
 * @param {String} label - Button label.
 * @param {String} [modifiers] - Button modifiers in space separated list
 * (primary, secondary, fill, text, dark, block, center, or reversed).
 * @param {Boolean} [showIcon] - Whether to show the button icon.
 * @param {Boolean} [hoverOnly] - Whether to show the icon on hover only.
 * @param {Boolean} [showStateIcon] - Show secondary button state, replaces text
 * and default icon with `stateIcon` slot.
 * @param {String} [type] - Type of button (button or submit).
 * @param {String} [url] - URL if link.
 *
 */
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'AccBtn',

  components: {
    IconArrowRight,
  },

  props: {
    classLabel: {
      type: String,
      default: '',
    },
    customElement: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
      required: false,
    },
    labelHtml: {
      type: String,
      default: '',
    },
    modifiers: {
      type: [String, Array],
      default: 'primary',
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    showStateIcon: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: '',
      validator: (value) => {
        return ['', 'button', 'submit'].includes(value)
      },
    },
    hoverOnly: {
      type: Boolean,
      default: false,
    },
    transitionHover: {
      type: Boolean,
      default: false,
    },
    url: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  computed: {

    /**
     * Compute element type.
     * @returns {String}
     */
    element() {
      if (this.customElement) {
        return this.customElement
      }

      return this.url ? 'a' : 'button'
    },

    /**
     * Compute label classes.
     * @returns {String}
     */
    labelClasses() {
      if (this.classLabel) {
        return this.classLabel
      }

      return 'text-other-button-l'
    },

    /**
     * Compute modifier classes.
     * @returns {String}
     */
    modifierClasses() {
      if (!this.modifiers) {
        return ''
      }

      let modifiers = []

      if (Array.isArray(this.modifiers)) {
        modifiers = this.modifiers
      } else {
        modifiers = this.modifiers.split(/[\s,]/g)
      }

      return modifiers.map((modifier) => {
        return `button--${modifier}`
      }).join(' ')
    },
  },
}
</script>
